import { type ComunicazioneAnteprimaDTO } from '@framework/api-client/api/inquiries-compile/schemas/comunicazioneAnteprimaDTO.ts';
import {
  type ErrorType,
  type IApiClientRequestOptions,
} from '@framework/api-client/client/apiClient.ts';
import { useMemo } from 'react';
import {
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
  useQuery,
} from 'react-query';
import { useCommunicationsContext } from '../../context/CommunicationsContext.tsx';

export type IGetHydrateQueryOptions = <
  TData = ComunicazioneAnteprimaDTO,
  TError = ErrorType<unknown>,
>(
  entityId: string,
  communicationId: string,
  options?: {
    query?: UseQueryOptions<ComunicazioneAnteprimaDTO, TError, TData>;
    request?: IApiClientRequestOptions | undefined;
  },
) => UseQueryOptions<ComunicazioneAnteprimaDTO, TError, TData> & {
  queryKey: QueryKey;
};

interface IProps {
  communicationTypeId: string | null | undefined;
  disabled?: boolean;
}

/**
 * Hook che attraverso i dati forniti dal context, permette di ottenere la
 * comunicazione idratata ("anteprima") con i placeholder sostituiti.
 */
export function useHydratedCommunicationContent(props: IProps) {
  const { communicationTypeId, disabled } = props;
  const {
    submissionId,
    taskId,
    procedureId,
    getHydrateCommunicationQueryOptions,
  } = useCommunicationsContext();

  const entityId = submissionId ?? taskId ?? procedureId;

  const queryOptions = useMemo(() => {
    if (!getHydrateCommunicationQueryOptions) {
      console.warn(
        '[useHydratedCommunicationContent] No hydrate query options',
      );
      return {
        enabled: false,
        queryKey: [],
      };
    }
    return getHydrateCommunicationQueryOptions?.(
      entityId ?? '<unknown>',
      communicationTypeId ?? '<unknown>',
      {
        query: {
          enabled: !!communicationTypeId && !!entityId && !disabled,
        },
      },
    );
  }, [
    communicationTypeId,
    disabled,
    entityId,
    getHydrateCommunicationQueryOptions,
  ]);

  const query = useQuery(queryOptions) as UseQueryResult<
    ComunicazioneAnteprimaDTO,
    ErrorType<unknown>
  > & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
